import { AlertCircle, Check, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ImportPreviewResponse } from '@jr2os/shared'
import Modal from '../ui/Modal'
import { formatCents } from '../../lib/money'

const STATUS_PILL: Record<ImportPreviewResponse['samples'][number]['status'], string> = {
  new: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  duplicate: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
  overlap: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
}

/**
 * Lo que entraría de un Excel, ANTES de que entre.
 *
 * Existe porque un Excel mal leído (una columna equivocada, un signo al revés, la
 * cuenta de otro banco) contaminaría la tesorería, y lo único que sabe el usuario es
 * lo que ve en su propia hoja. Se enseña qué columna se ha tomado como qué, si los
 * saldos del banco encadenan y cuántos movimientos ya estaban —por la conexión, por
 * un N43 o por otro Excel—, para que compare con lo que tiene delante.
 */
export default function ImportPreviewModal({
  preview,
  busy,
  onConfirm,
  onClose,
}: {
  preview: ImportPreviewResponse
  busy: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const { t } = useTranslation(['banking', 'common'])
  const { detected } = preview
  const chain = detected?.balanceChain ?? null
  const chainOk = chain !== null && chain.checked > 0 && chain.ok === chain.checked
  const nothingNew = preview.willImport === 0

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      title={t('importPreview.title')}
      description={preview.fileName}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-3 text-sm">
        <p className="t-primary">
          {preview.total === 0
            ? t('importPreview.nothingInFile')
            : nothingNew
              ? t('importPreview.nothingNew', { total: preview.total })
              : t('importPreview.summary', {
                  count: preview.willImport,
                  willImport: preview.willImport,
                  total: preview.total,
                })}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <Meta
            label={t('import.period')}
            value={`${preview.fromDate.slice(0, 10)} → ${preview.toDate.slice(0, 10)}`}
          />
          <Meta label={t('totals.in')} value={formatCents(preview.inCents)} />
          <Meta label={t('totals.out')} value={formatCents(preview.outCents)} />
          <Meta
            label={t('import.closingBalance')}
            value={
              preview.closingBalanceCents === null ? '—' : formatCents(preview.closingBalanceCents)
            }
          />
        </div>

        {/* Lo que no va a entrar, y por qué. */}
        {(preview.duplicates > 0 || preview.overlapping > 0) && (
          <ul className="text-xs t-muted list-disc pl-4 space-y-0.5">
            {preview.duplicates > 0 && (
              <li>{t('importPreview.duplicates', { count: preview.duplicates })}</li>
            )}
            {preview.overlapping > 0 && (
              <li>{t('importPreview.overlapping', { count: preview.overlapping })}</li>
            )}
          </ul>
        )}

        {/* La comprobación de que es la cuenta que se ha elegido. */}
        {preview.accountMatches === true && (
          <Notice tone="ok">
            {t('importPreview.accountOk', { account: preview.declaredAccount })}
          </Notice>
        )}
        {preview.accountMatches === null && (
          <Notice tone="warn">{t('importPreview.accountUnknown')}</Notice>
        )}

        {detected && (
          <div className="rounded-lg border border-base p-3 text-xs space-y-1.5">
            <p className="font-medium t-primary">{t('importPreview.detectedTitle')}</p>
            <p className="t-muted">
              {t('importPreview.detectedWhere', { sheet: detected.sheet, row: detected.headerRow })}
              {detected.reversed ? ` ${t('importPreview.reversed')}` : ''}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
              {detected.columns.map((c) => (
                <li key={c.role} className="flex gap-1.5">
                  <span className="t-muted">
                    {t(`importPreview.roles.${c.role}`, { defaultValue: c.role })}
                  </span>
                  <span className="t-primary font-mono truncate">← {c.labels.join(' + ')}</span>
                </li>
              ))}
            </ul>
            {chain && chain.checked > 0 && (
              <p
                className={
                  chainOk
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-amber-700 dark:text-amber-300'
                }
              >
                {chainOk
                  ? t('importPreview.chainOk', { ok: chain.ok, checked: chain.checked })
                  : t('importPreview.chainBad', { ok: chain.ok, checked: chain.checked })}
              </p>
            )}
          </div>
        )}

        {preview.diffCents !== null &&
          preview.ledgerCents !== null &&
          preview.closingBalanceCents !== null && (
            <p className="text-xs t-muted">
              {t('import.balanceDetail', {
                bank: formatCents(preview.closingBalanceCents),
                ledger: formatCents(preview.ledgerCents),
                diff: formatCents(preview.diffCents),
              })}
            </p>
          )}

        {preview.warnings.length > 0 && (
          <ul className="text-xs text-amber-700 dark:text-amber-300 list-disc pl-4 space-y-0.5">
            {preview.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}

        {/* Lo más reciente primero, como en la pantalla del banco. */}
        <div className="overflow-x-auto rounded-lg border border-base max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 surface-deep">
              <tr className="text-left t-muted uppercase tracking-wider">
                <th className="px-2 py-1.5">{t('columns.date')}</th>
                <th className="px-2 py-1.5">{t('columns.concept')}</th>
                <th className="px-2 py-1.5 text-right">{t('columns.amount')}</th>
                <th className="px-2 py-1.5">{t('columns.status')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.samples.map((s, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="px-2 py-1 font-mono whitespace-nowrap">
                    {s.operationDate.slice(0, 10)}
                  </td>
                  <td className="px-2 py-1 t-primary max-w-[18rem] truncate" title={s.concept}>
                    {s.concept}
                  </td>
                  <td
                    className={`px-2 py-1 font-mono text-right whitespace-nowrap ${
                      s.amountCents < 0 ? 'text-rose-600 dark:text-rose-300' : 't-primary'
                    }`}
                  >
                    {formatCents(s.amountCents)}
                  </td>
                  <td className="px-2 py-1">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_PILL[s.status]}`}
                    >
                      {t(`importPreview.status.${s.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview.total > preview.samples.length && (
          <p className="text-[11px] t-faint">
            {t('importPreview.moreRows', { shown: preview.samples.length, total: preview.total })}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-base text-sm hover:bg-hover t-primary"
          >
            {t('common:actions.cancel')}
          </button>
          {!nothingNew && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-jr2-600 hover:bg-jr2-500 text-white text-sm disabled:opacity-60"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {t('importPreview.confirm', { count: preview.willImport })}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div className="t-primary font-mono">{value}</div>
    </div>
  )
}

function Notice({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  return (
    <div
      className={[
        'rounded-lg border p-2.5 text-xs flex items-start gap-1.5',
        tone === 'ok'
          ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
          : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300',
      ].join(' ')}
    >
      {tone === 'ok' ? (
        <Check size={13} className="mt-0.5 shrink-0" />
      ) : (
        <AlertCircle size={13} className="mt-0.5 shrink-0" />
      )}
      <span>{children}</span>
    </div>
  )
}
